// src/pages/RegistroLocal.js
//
// D-23 — Registro de un negocio que compra SOLO HolaVeci Local.
//
// Pasos:
//   1. Teléfono  → OTP por SMS (propósito "registro").
//   2. Negocio   → nombre, categoría, ubicación (municipio, dirección, pin).
//   3. Cuenta    → representante, correo, contraseña, código de guía opcional.
//   4. Confirmar → aceptación de documentos legales y envío.
//
// Al terminar inicia sesión y entra al POS, donde el gate lo lleva a subir
// cédula y RUT. Después: revisión → pago del plan → activar.

import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate, Link as RouterLink } from 'react-router-dom';
import {
  Box, Paper, Typography, Stepper, Step, StepLabel, TextField, Button, Stack,
  Alert, MenuItem, Grid, InputAdornment, IconButton, CircularProgress, Link, Divider,
} from '@mui/material';
import Visibility from '@mui/icons-material/Visibility';
import VisibilityOff from '@mui/icons-material/VisibilityOff';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import LogoHolaVeciLocal from '../components/marca/LogoHolaVeciLocal';
import SelectorMunicipio from '../components/registro/SelectorMunicipio';
import AceptacionLegal from '../components/registro/AceptacionLegal';
import MapaSucursal from '../components/MapaSucursal';
import { registroLocal } from '../api/holaveciLocalApi';
import { useAuth } from '../context/AuthContext';

const PASOS = ['Teléfono', 'Negocio', 'Cuenta', 'Confirmar'];
const DOCS_LEGALES = ['TC_NEGOCIO', 'POLITICA_DATOS', 'AUTORIZACION_DATOS'];
const TIPOS_DOCUMENTO = [
  { value: 'CC',  label: 'Cédula de ciudadanía' },
  { value: 'CE',  label: 'Cédula de extranjería' },
  { value: 'PPT', label: 'Permiso por protección temporal' },
  { value: 'PA',  label: 'Pasaporte' },
];

/** 3001234567 / 573001234567 / +57 300 123 4567 → +573001234567 */
const normalizarTelefono = (raw) => {
  const d = (raw || '').replace(/\D/g, '');
  if (d.length === 10) return `+57${d}`;
  if (d.length === 12 && d.startsWith('57')) return `+${d}`;
  return null;
};

const correoValido = (c) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test((c || '').trim());

export default function RegistroLocal() {
  const navigate = useNavigate();
  const { login } = useAuth();
  const [paso, setPaso] = useState(0);
  const [error, setError] = useState('');

  // ── Paso 1: teléfono + OTP ──
  const [telefono, setTelefono]   = useState('');
  const [otpEnviado, setOtpEnv]   = useState(false);
  const [codigo, setCodigo]       = useState('');
  const [espera, setEspera]       = useState(0);
  // Celular que ya tiene cuenta en HolaVeci: { estado, nombre }.
  const [cuentaExistente, setCuentaExistente] = useState(null);
  const [trabajando, setTrab]     = useState(false);

  // ── Paso 2: negocio ──
  const [categorias, setCategorias] = useState([]);
  const [negocio, setNegocio] = useState({
    nombre: '', categoriaNegocioId: '', municipioId: '',
    direccion: '', barrio: '', lat: null, lng: null,
  });
  const [muniInfo, setMuniInfo] = useState(null);

  // ── Paso 3: cuenta ──
  const [cuenta, setCuenta] = useState({
    nombre: '', apellido: '', tipoDocumento: 'CC', numeroDocumento: '',
    correo: '', password: '', password2: '', codigoGuia: '',
  });
  const [verPwd, setVerPwd] = useState(false);
  const [guia, setGuia] = useState(null);           // { nombreGuia, municipio } si validó
  const [validandoGuia, setValidandoGuia] = useState(false);

  // ── Paso 4: legales ──
  const [aceptaciones, setAceptaciones] = useState({});

  useEffect(() => {
    (async () => {
      try {
        const r = await registroLocal.categorias();
        setCategorias(Array.isArray(r) ? r : (r?.items || []));
      } catch { /* el paso 2 muestra el selector vacío con aviso */ }
    })();
  }, []);

  useEffect(() => {
    if (espera <= 0) return undefined;
    const t = setTimeout(() => setEspera(espera - 1), 1000);
    return () => clearTimeout(t);
  }, [espera]);

  const telefonoE164 = useMemo(() => normalizarTelefono(telefono), [telefono]);

  const enviarOtp = async () => {
    setError('');
    if (!telefonoE164) { setError('Escribe un celular colombiano de 10 dígitos.'); return; }
    setTrab(true);
    try {
      const r = await registroLocal.enviarOtp(telefonoE164);
      if (r.enviado === false) {
        setError(r.mensaje || 'No se pudo enviar el código.');
        if (r.esperarSegundos) setEspera(r.esperarSegundos);
        return;
      }
      setOtpEnv(true);
      setEspera(r.esperarSegundos || 60);
    } catch (e) {
      setError(e.mensajeUsuario || 'No se pudo enviar el código.');
    } finally { setTrab(false); }
  };

  const verificarOtp = async () => {
    setError('');
    if (!/^\d{6}$/.test(codigo)) { setError('El código tiene 6 dígitos.'); return; }
    setTrab(true);
    try {
      const r = await registroLocal.verificarOtp(telefonoE164, codigo);
      if (!r.valido) { setError(r.mensaje || 'Código incorrecto.'); return; }

      // El celular ya puede tener cuenta (p. ej. HolaVeci Negocio): en ese
      // caso no se sigue con el registro.
      const v = await registroLocal.verificarTelefono(telefonoE164);
      if (v.estado && v.estado !== 'disponible') {
        setCuentaExistente({ estado: v.estado, nombre: v.nombreExistente });
        return;
      }
      setPaso(1);
    } catch (e) {
      setError(e.mensajeUsuario || 'No se pudo verificar el código.');
    } finally { setTrab(false); }
  };

  const validarGuia = async () => {
    const c = cuenta.codigoGuia.trim();
    setGuia(null);
    if (!c) return true;
    setValidandoGuia(true);
    try {
      const r = await registroLocal.validarCodigoGuia(c);
      setGuia({ nombreGuia: r.nombreGuia, municipio: r.municipio, codigo: r.codigo });
      setCuenta(s => ({ ...s, codigoGuia: r.codigo || c.toUpperCase() }));
      return true;
    } catch (e) {
      setError(e.mensajeUsuario || 'El código del guía no es válido. Revísalo o déjalo vacío.');
      return false;
    } finally { setValidandoGuia(false); }
  };

  const validarPasoNegocio = () => {
    if (negocio.nombre.trim().length < 3) return 'Escribe el nombre del negocio.';
    if (!negocio.categoriaNegocioId) return 'Elige la categoría del negocio.';
    if (!negocio.municipioId) return 'Elige el municipio.';
    if (negocio.direccion.trim().length < 5) return 'Escribe la dirección del negocio.';
    if (negocio.lat == null || negocio.lng == null) return 'Ubica tu negocio en el mapa.';
    return null;
  };

  const validarPasoCuenta = () => {
    if (!cuenta.nombre.trim() || !cuenta.apellido.trim()) return 'Escribe nombres y apellidos del representante.';
    if (!/^[A-Za-z0-9]{4,20}$/.test(cuenta.numeroDocumento.trim())) return 'El número de documento no es válido.';
    if (!correoValido(cuenta.correo)) return 'Escribe un correo válido.';
    if (cuenta.password.length < 6) return 'La contraseña debe tener al menos 6 caracteres.';
    if (cuenta.password !== cuenta.password2) return 'Las contraseñas no coinciden.';
    return null;
  };

  const siguiente = async () => {
    setError('');
    if (paso === 1) {
      const e = validarPasoNegocio();
      if (e) { setError(e); return; }
    }
    if (paso === 2) {
      const e = validarPasoCuenta();
      if (e) { setError(e); return; }
      if (!(await validarGuia())) return;
    }
    setPaso(p => p + 1);
  };

  const todosAceptados = DOCS_LEGALES.every(c => aceptaciones[c]?.aceptado);

  const registrar = async () => {
    setError('');
    if (!todosAceptados) { setError('Debes aceptar los documentos para continuar.'); return; }
    setTrab(true);
    try {
      await registroLocal.registrar({
        nombre: negocio.nombre.trim(),
        nombreRepresentante: cuenta.nombre.trim(),
        apellidoRepresentante: cuenta.apellido.trim(),
        tipoDocumentoRepresentante: cuenta.tipoDocumento,
        numeroDocumentoRepresentante: cuenta.numeroDocumento.trim(),
        categoriaNegocioId: Number(negocio.categoriaNegocioId),
        direccion: negocio.direccion.trim(),
        barrio: negocio.barrio.trim() || null,
        municipioID: Number(negocio.municipioId),
        latitud: negocio.lat,
        longitud: negocio.lng,
        telefono: telefonoE164,
        correo: cuenta.correo.trim().toLowerCase(),
        password: cuenta.password,
        codigoGuia: cuenta.codigoGuia.trim() || null,
        aceptaciones: DOCS_LEGALES.map(c => ({
          codigo: aceptaciones[c].codigo,
          checksumAceptado: aceptaciones[c].checksumAceptado,
        })),
      });

      const r = await login(telefonoE164, cuenta.password);
      if (r.ok) {
        navigate('/dashboard', { replace: true });
      } else {
        navigate('/login', { replace: true });
      }
    } catch (e) {
      setError(e.mensajeUsuario || 'No se pudo completar el registro.');
    } finally { setTrab(false); }
  };

  const categoriaNombre = categorias.find(c => String(c.id) === String(negocio.categoriaNegocioId))?.nombre;

  return (
    <Box sx={{
      minHeight: '100vh', py: { xs: 2, md: 5 }, px: 2,
      background: 'linear-gradient(135deg, #083025 0%, #0F4C3A 55%, #2E6E5A 100%)',
    }}>
      <Paper elevation={0} sx={{
        p: { xs: 2.5, sm: 4 }, maxWidth: 820, mx: 'auto', borderRadius: 3,
        border: '1px solid rgba(15, 76, 58, 0.14)',
      }}>
        <Stack alignItems="center" spacing={1} sx={{ mb: 3, textAlign: 'center' }}>
          <LogoHolaVeciLocal size={32} compacto />
          <Typography variant="h5">Registra tu negocio</Typography>
          <Typography variant="body2" color="text.secondary">
            Caja, inventario y ventas de mostrador. Después validamos tus documentos y activas tu plan.
          </Typography>
        </Stack>

        <Stepper activeStep={paso} alternativeLabel sx={{ mb: 3 }}>
          {PASOS.map(p => <Step key={p}><StepLabel>{p}</StepLabel></Step>)}
        </Stepper>

        {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>{error}</Alert>}

        {/* ── Celular con cuenta existente ── */}
        {paso === 0 && cuentaExistente && (
          <Stack spacing={2} sx={{ maxWidth: 520, mx: 'auto' }}>
            {cuentaExistente.estado === 'holaveci_negocio' && (
              <>
                <Alert severity="info">
                  {cuentaExistente.nombre ? `Hola, ${cuentaExistente.nombre}. ` : ''}
                  Este celular ya tiene <b>HolaVeci Negocio</b>. No necesitas registrarte de nuevo:
                  agrega HolaVeci Local desde tu app <b>HolaVeci Negocio → Perfil → Agregar HolaVeci Local</b>.
                  Pagarás un solo plan, HolaVeci 360°.
                </Alert>
                <Typography variant="body2" color="text.secondary" align="center">
                  Cuando el pago se confirme, entra aquí con el mismo celular y contraseña para activar HolaVeci Local.
                </Typography>
                <Button variant="contained" size="large" component={RouterLink} to="/login">
                  Ir a iniciar sesión
                </Button>
              </>
            )}
            {cuentaExistente.estado === 'holaveci_local' && (
              <>
                <Alert severity="info">
                  Este celular ya tiene una cuenta de <b>HolaVeci Local</b>. Inicia sesión para continuar
                  donde quedaste.
                </Alert>
                <Button variant="contained" size="large" component={RouterLink} to="/login">
                  Iniciar sesión
                </Button>
              </>
            )}
            {cuentaExistente.estado === 'otra_cuenta' && (
              <Alert severity="warning">
                Este celular ya está registrado en HolaVeci con otro tipo de cuenta (veci, domiciliario o
                guía). Para registrar tu negocio usa otro número celular.
              </Alert>
            )}
            {cuentaExistente.estado === 'cuenta_cerrada' && (
              <Alert severity="warning">
                Este celular pertenece a una cuenta cerrada. Escríbenos a <b>soporte@holaveci.com</b> para
                ayudarte.
              </Alert>
            )}
            <Button onClick={() => {
              setCuentaExistente(null); setOtpEnv(false); setCodigo(''); setTelefono('');
            }}>
              Usar otro número
            </Button>
          </Stack>
        )}

        {/* ── 1. Teléfono ── */}
        {paso === 0 && !cuentaExistente && (
          <Stack spacing={2} sx={{ maxWidth: 420, mx: 'auto' }}>
            <TextField
              label="Celular" placeholder="300 123 4567" fullWidth
              name="tel-national" autoComplete="tel-national" inputProps={{ inputMode: 'tel' }}
              value={telefono} disabled={otpEnviado || trabajando}
              onChange={(e) => setTelefono(e.target.value)}
              InputProps={{ startAdornment: <InputAdornment position="start">+57</InputAdornment> }}
              helperText="Con este número inicias sesión. Te enviaremos un código por SMS."
            />
            {!otpEnviado ? (
              <Button variant="contained" size="large" onClick={enviarOtp} disabled={trabajando}>
                {trabajando ? 'Enviando…' : 'Enviar código'}
              </Button>
            ) : (
              <>
                <TextField
                  label="Código de 6 dígitos" fullWidth autoFocus
                  value={codigo} disabled={trabajando}
                  name="one-time-code" autoComplete="one-time-code"
                  inputProps={{ inputMode: 'numeric', maxLength: 6 }}
                  onChange={(e) => setCodigo(e.target.value.replace(/\D/g, ''))}
                  onKeyDown={(e) => { if (e.key === 'Enter') verificarOtp(); }}
                />
                <Button variant="contained" size="large" onClick={verificarOtp} disabled={trabajando}>
                  {trabajando ? 'Verificando…' : 'Verificar'}
                </Button>
                <Stack direction="row" justifyContent="space-between">
                  <Button size="small" onClick={() => { setOtpEnv(false); setCodigo(''); }} disabled={trabajando}>
                    Cambiar número
                  </Button>
                  <Button size="small" onClick={enviarOtp} disabled={trabajando || espera > 0}>
                    {espera > 0 ? `Reenviar en ${espera}s` : 'Reenviar código'}
                  </Button>
                </Stack>
              </>
            )}
          </Stack>
        )}

        {/* ── 2. Negocio ── */}
        {paso === 1 && (
          <Stack spacing={2}>
            <Grid container spacing={2}>
              <Grid item xs={12} md={7}>
                <TextField required fullWidth label="Nombre del negocio"
                  value={negocio.nombre} inputProps={{ maxLength: 200 }}
                  onChange={(e) => setNegocio(s => ({ ...s, nombre: e.target.value }))} />
              </Grid>
              <Grid item xs={12} md={5}>
                <TextField required select fullWidth label="Categoría"
                  value={negocio.categoriaNegocioId}
                  onChange={(e) => setNegocio(s => ({ ...s, categoriaNegocioId: e.target.value }))}
                  helperText={categorias.length === 0 ? 'No se pudieron cargar las categorías.' : ' '}>
                  {categorias.map(c => (
                    <MenuItem key={c.id} value={c.id}>{c.emoji ? `${c.emoji}  ` : ''}{c.nombre}</MenuItem>
                  ))}
                </TextField>
              </Grid>
            </Grid>

            <SelectorMunicipio
              value={negocio.municipioId}
              onChange={(v) => setNegocio(s => ({ ...s, municipioId: v }))}
              onSeleccion={setMuniInfo}
            />

            <Grid container spacing={2}>
              <Grid item xs={12} md={7}>
                <TextField required fullWidth label="Dirección" placeholder="Cra 27 # 52-33"
                  value={negocio.direccion} inputProps={{ maxLength: 500 }}
                  onChange={(e) => setNegocio(s => ({ ...s, direccion: e.target.value }))} />
              </Grid>
              <Grid item xs={12} md={5}>
                <TextField fullWidth label="Barrio (opcional)"
                  value={negocio.barrio} inputProps={{ maxLength: 200 }}
                  onChange={(e) => setNegocio(s => ({ ...s, barrio: e.target.value }))} />
              </Grid>
            </Grid>

            <Box>
              <Typography variant="subtitle2" sx={{ mb: 1 }}>Ubicación del negocio *</Typography>
              <MapaSucursal
                lat={negocio.lat} lng={negocio.lng}
                permitirBusqueda={false}
                centroInicial={muniInfo}
                onCoordsChange={(la, ln) => setNegocio(s => ({ ...s, lat: la, lng: ln }))}
                notify={(m) => setError(m)}
              />
            </Box>
          </Stack>
        )}

        {/* ── 3. Cuenta ── */}
        {paso === 2 && (
          <Stack spacing={2}>
            <Typography variant="subtitle2">Representante legal</Typography>
            <Grid container spacing={2}>
              <Grid item xs={12} md={6}>
                <TextField required fullWidth label="Nombres" value={cuenta.nombre}
                  name="given-name" autoComplete="given-name"
                  onChange={(e) => setCuenta(s => ({ ...s, nombre: e.target.value }))} />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField required fullWidth label="Apellidos" value={cuenta.apellido}
                  name="family-name" autoComplete="family-name"
                  onChange={(e) => setCuenta(s => ({ ...s, apellido: e.target.value }))} />
              </Grid>
              <Grid item xs={12} md={5}>
                <TextField required select fullWidth label="Tipo de documento" value={cuenta.tipoDocumento}
                  onChange={(e) => setCuenta(s => ({ ...s, tipoDocumento: e.target.value }))}>
                  {TIPOS_DOCUMENTO.map(t => <MenuItem key={t.value} value={t.value}>{t.label}</MenuItem>)}
                </TextField>
              </Grid>
              <Grid item xs={12} md={7}>
                <TextField required fullWidth label="Número de documento" value={cuenta.numeroDocumento}
                  name="documento" autoComplete="off"
                  inputProps={{ maxLength: 20 }}
                  onChange={(e) => setCuenta(s => ({ ...s, numeroDocumento: e.target.value.replace(/[^A-Za-z0-9]/g, '') }))}
                  helperText="Debe coincidir con la cédula que subirás después." />
              </Grid>
            </Grid>

            <Divider />
            <Typography variant="subtitle2">Acceso</Typography>
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <TextField required fullWidth type="email" label="Correo" value={cuenta.correo}
                  name="email" autoComplete="email"
                  onChange={(e) => setCuenta(s => ({ ...s, correo: e.target.value }))}
                  helperText="Te enviaremos la confirmación y avisos de tu cuenta." />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField required fullWidth label="Contraseña" type={verPwd ? 'text' : 'password'}
                  name="new-password" autoComplete="new-password"
                  value={cuenta.password}
                  onChange={(e) => setCuenta(s => ({ ...s, password: e.target.value }))}
                  InputProps={{ endAdornment: (
                    <InputAdornment position="end">
                      <IconButton onClick={() => setVerPwd(v => !v)} edge="end">
                        {verPwd ? <VisibilityOff /> : <Visibility />}
                      </IconButton>
                    </InputAdornment>
                  ) }} />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField required fullWidth label="Repite la contraseña" type={verPwd ? 'text' : 'password'}
                  name="new-password-confirm" autoComplete="new-password"
                  value={cuenta.password2}
                  onChange={(e) => setCuenta(s => ({ ...s, password2: e.target.value }))} />
              </Grid>
            </Grid>

            <Divider />
            <Typography variant="subtitle2">¿Te acompañó un guía de HolaVeci? (opcional)</Typography>
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} alignItems={{ sm: 'flex-start' }}>
              <TextField fullWidth label="Código del guía" value={cuenta.codigoGuia}
                name="codigo-guia" autoComplete="off"
                inputProps={{ maxLength: 20 }}
                onChange={(e) => { setGuia(null); setCuenta(s => ({ ...s, codigoGuia: e.target.value.toUpperCase() })); }}
                helperText="Si no tienes código, déjalo vacío." />
              <Button variant="outlined" onClick={validarGuia}
                disabled={validandoGuia || !cuenta.codigoGuia.trim()} sx={{ mt: { sm: 1 } }}>
                {validandoGuia ? 'Validando…' : 'Validar'}
              </Button>
            </Stack>
            {guia && (
              <Alert icon={<CheckCircleIcon />} severity="success">
                Guía: <b>{guia.nombreGuia}</b>{guia.municipio ? ` · ${guia.municipio}` : ''}
              </Alert>
            )}
          </Stack>
        )}

        {/* ── 4. Confirmar ── */}
        {paso === 3 && (
          <Stack spacing={2}>
            <Paper variant="outlined" sx={{ p: 2 }}>
              <Grid container spacing={1}>
                <Grid item xs={12} sm={6}><Typography variant="caption" color="text.secondary">Negocio</Typography>
                  <Typography>{negocio.nombre}{categoriaNombre ? ` · ${categoriaNombre}` : ''}</Typography></Grid>
                <Grid item xs={12} sm={6}><Typography variant="caption" color="text.secondary">Dirección</Typography>
                  <Typography>{negocio.direccion}{muniInfo?.municipioNombre ? `, ${muniInfo.municipioNombre}` : ''}</Typography></Grid>
                <Grid item xs={12} sm={6}><Typography variant="caption" color="text.secondary">Representante</Typography>
                  <Typography>{cuenta.nombre} {cuenta.apellido} · {cuenta.tipoDocumento} {cuenta.numeroDocumento}</Typography></Grid>
                <Grid item xs={12} sm={6}><Typography variant="caption" color="text.secondary">Contacto</Typography>
                  <Typography>{telefonoE164} · {cuenta.correo}</Typography></Grid>
                <Grid item xs={12}><Typography variant="caption" color="text.secondary">Guía</Typography>
                  <Typography>{guia ? `${guia.nombreGuia} (${guia.codigo})` : 'Sin guía'}</Typography></Grid>
              </Grid>
            </Paper>

            <Box>
              {DOCS_LEGALES.map(c => (
                <AceptacionLegal key={c} codigo={c} aceptado={aceptaciones[c]?.aceptado}
                  onChange={(a) => setAceptaciones(s => ({ ...s, [c]: a }))} />
              ))}
            </Box>

            <Alert severity="info">
              Después del registro subirás tu cédula y el RUT. Cuando los validemos podrás pagar
              tu plan y activar HolaVeci Local.
            </Alert>
          </Stack>
        )}

        {/* ── Navegación ── */}
        {paso > 0 && (
          <Stack direction="row" justifyContent="space-between" sx={{ mt: 3 }}>
            <Button onClick={() => { setError(''); setPaso(p => p - 1); }} disabled={trabajando || paso === 1}>
              Atrás
            </Button>
            {paso < 3 ? (
              <Button variant="contained" onClick={siguiente} disabled={validandoGuia}>
                Siguiente
              </Button>
            ) : (
              <Button variant="contained" onClick={registrar} disabled={trabajando || !todosAceptados}
                startIcon={trabajando ? <CircularProgress size={18} color="inherit" /> : null}>
                {trabajando ? 'Registrando…' : 'Crear mi cuenta'}
              </Button>
            )}
          </Stack>
        )}

        <Typography variant="body2" align="center" sx={{ mt: 3 }}>
          ¿Ya tienes cuenta?{' '}
          <Link component={RouterLink} to="/login">Inicia sesión</Link>
        </Typography>
      </Paper>
    </Box>
  );
}
